/* DealPlaybooksPanel.jsx — Deal Room intelligence rail mount.
 *
 * Renders every active operational playbook for the current deal.
 * Mounted inside the Deal Room right-hand intelligence rail.
 *
 * Doctrine:
 *  • Calm operational presence — auto-hides when no playbook is active.
 *  • Reads from /api/playbooks/deals/{deal_id} on mount + 60s polling.
 *  • Step ticks call /api/playbooks/runs/{run_id}/steps/{step_id}/complete
 *    and re-fetch the playbook so the audit trail stays the source of truth.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList } from "lucide-react";
import { api } from "../../lib/api";
import { PlaybookCard } from "./PlaybookCard";

const POLL_MS = 60_000;

export function DealPlaybooksPanel({ dealId }) {
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const refetch = useCallback(async () => {
    if (!dealId) return;
    try {
      const res = await api.get(`/api/playbooks/deals/${dealId}`);
      setPlaybooks(res.data?.playbooks || []);
      setError(null);
    } catch (e) {
      // 403 just means the user isn't leadership — degrade silently.
      if (e?.response?.status !== 403) {
        setError(e?.response?.data?.detail || "Could not load playbooks");
      }
      setPlaybooks([]);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { refetch(); }, [refetch]);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") refetch();
    };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [refetch]);

  const handleCompleteStep = async (runId, stepId) => {
    try {
      const res = await api.post(
        `/api/playbooks/runs/${runId}/steps/${stepId}/complete`,
        {},
      );
      if (res.data?.already_complete) {
        toast("Step already recorded as complete.");
      } else {
        toast.success("Step recorded · audit trail updated.");
      }
      refetch();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not record step. Please retry.",
      );
    }
  };

  if (loading || error) return null;
  if (!playbooks.length) return null;

  return (
    <section className="space-y-3" data-testid="dealroom-playbooks-panel">
      <div className="flex items-center gap-1.5">
        <ClipboardList size={11} className="text-violet-600" strokeWidth={2.3} />
        <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-violet-700">
          Operational Playbooks
        </p>
        <span className="text-[10.5px] text-slate-400">
          · {playbooks.length} active
        </span>
      </div>
      <div className="space-y-3">
        {playbooks.map((pb) => (
          <PlaybookCard
            key={pb.run_id}
            playbook={pb}
            defaultOpen={pb.priority === "critical"}
            compact
            onCompleteStep={(stepId) => handleCompleteStep(pb.run_id, stepId)}
            onReviewDeal={(did) => navigate(`/deal-rooms/${did}`)}
          />
        ))}
      </div>
    </section>
  );
}

export default DealPlaybooksPanel;
